/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useContext } from "react";

import moment from "moment";
import { v7 as uuidv7 } from "uuid";
import LanguageContext from "@/context/language/LanguageContext";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import { useRangeSelection } from "../../../shared/hooks/useRangeSelection";
import { bubble, cell } from "../../../shared/styles/useDatePickerStyles";
import { PICKING_STATE } from "../../../shared/types";
import { dayGrid } from "../dayRangePickerStyles";
import { useDayStripStyle } from "../hooks/useDayStripStyle";
import { DayViewProps } from "../types";

const DayView = ({
  year,
  month,
  endDay,
  startDay,
  hoverDay,
  picking,
  maxDate,
  minDate,
  disableSameDay,
  isRangePicker = true,
  nthDay,
  originalStartDay,
  shouldDisableDate,
  onDayClick,
  cancelHoverClear,
  setHoverDay
}: DayViewProps) => {
  const now = new Date();

  const { language } = useContext(LanguageContext);

  const { stripStyle } = useDayStripStyle();

  const isDayDisabled = (
    yearValue: number,
    monthValue: number,
    dayValue: number
  ): boolean => {
    const date = moment({
      year: yearValue,
      month: monthValue,
      day: dayValue
    });

    const beforeMin = minDate && date.isBefore(minDate.clone().startOf("day"));
    const afterMax = maxDate && date.isAfter(maxDate.clone().startOf("day"));

    return Boolean(beforeMin || afterMax || shouldDisableDate?.(date.toDate()));
  };

  const isDayKeyDisabled = (dayKey: number): boolean => {
    const date = new Date(dayKey);
    return isDayDisabled(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const { rangeStart, rangeEnd, isPreview, handleHover, getCellState } =
    useRangeSelection({
      startValue: startDay,
      endValue: endDay,
      hoverValue: hoverDay,
      picking,
      isDisabled: isDayKeyDisabled,
      setHoverValue: setHoverDay,
      cancelHoverClear
    });

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weekStart = moment.localeData(language).firstDayOfWeek();
  const adjustedFirstDay = (firstDayOfMonth - weekStart + 7) % 7;

  const gridCells: Array<number | null> = [
    ...Array.from({ length: adjustedFirstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1)
  ];

  const isOriginalStartDayHighlighted = (dayKey: number): boolean => {
    if (originalStartDay == null || startDay == null) return false;

    const day = moment(dayKey).startOf("day");
    const today = moment().startOf("day");
    const startDate = moment(startDay).startOf("day");
    const currentMonth = day.month();
    const currentYear = day.year();

    const lastDayOfMonth = moment([currentYear, currentMonth])
      .endOf("month")
      .date();
    const isSameOrAfterStartMonth =
      day.isSame(startDate, "month") || day.isAfter(startDate, "month");

    const dayMatches =
      originalStartDay <= lastDayOfMonth
        ? day.date() === originalStartDay
        : day.date() === lastDayOfMonth;

    return day.isAfter(today, "day") && isSameOrAfterStartMonth && dayMatches;
  };

  const isNthDayHighlighted = (dayKey: number): boolean => {
    if (nthDay == null || nthDay <= 0 || startDay == null) return false;

    const day = moment(dayKey).startOf("day");
    const today = moment().startOf("day");
    const startDate = moment(startDay).startOf("day");

    const diffDays = day.diff(startDate, "days");

    return (
      day.isAfter(today, "day") &&
      day.isAfter(startDate, "day") &&
      diffDays % nthDay === 0
    );
  };

  const getCellProps = (dayValue: number) => {
    const toDayKey = (
      yearValue: number,
      monthValue: number,
      dayValue: number
    ): number => new Date(yearValue, monthValue, dayValue).getTime();

    const dayKey = toDayKey(year, month, dayValue);
    const todayValue = toDayKey(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    const state = getCellState({ value: dayKey, todayValue });
    const strip = stripStyle(dayKey, { rangeStart, rangeEnd, isPreview });

    return {
      dayKey,
      strip,
      bubbleProps: {
        ...state,
        isDisabled: isDayDisabled(year, month, dayValue),
        disableHoverOnSameDay: Boolean(
          disableSameDay &&
          picking === PICKING_STATE.End &&
          dayKey === rangeStart
        )
      }
    };
  };

  return (
    <div onMouseEnter={cancelHoverClear} onMouseLeave={() => setHoverDay(null)}>
      <div css={css(dayGrid)}>
        {gridCells.map(dayValue => {
          if (dayValue === null) {
            return <div key={`blank-${uuidv7()}`} css={css(cell)} />;
          }

          const { dayKey, strip, bubbleProps } = getCellProps(dayValue);

          return (
            <div
              key={dayKey}
              css={css(cell)}
              onMouseEnter={() => handleHover(dayKey)}
            >
              <div style={strip} />

              <Typography
                variant="body1Medium"
                css={css(bubble(bubbleProps), {
                  width:
                    isRangePicker &&
                    (bubbleProps.isInRange ||
                      bubbleProps.isStart ||
                      bubbleProps.isEnd ||
                      bubbleProps.isPreviewEnd)
                      ? undefined
                      : "32px",
                  height: "32px",

                  ...((isNthDayHighlighted(dayKey) ||
                    isOriginalStartDayHighlighted(dayKey)) && {
                    boxShadow: `inset 0 0 0 2px ${colors.blue700}`
                  })
                })}
                onClick={() => !bubbleProps.isDisabled && onDayClick(dayKey)}
              >
                {dayValue}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DayView;
